// LeaseForm — creates or edits a lease. Picks tenant from this property's tenants.
import { useState, useEffect } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const inOneYear = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

const UTIL_OPTIONS = ["water", "trash", "internet", "gas", "electric"];

export default function LeaseForm({ open, onOpenChange, propertyId, tenants, lease, defaultCurrency = "USD", onSaved }) {
  const editing = !!lease;
  const [form, setForm] = useState({
    tenant_id: tenants?.[0]?.id || "",
    start_date: today(),
    end_date: inOneYear(),
    monthly_rent: "",
    currency: defaultCurrency,
    security_deposit: "",
    pet_deposit: "0",
    pets_allowed: false,
    late_fee_amount: "0",
    late_fee_grace_days: 5,
    utilities_included: [],
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && lease) {
      setForm({
        tenant_id: lease.tenant_id || "",
        start_date: lease.start_date || today(),
        end_date: lease.end_date || inOneYear(),
        monthly_rent: lease.monthly_rent ?? "",
        currency: lease.currency || defaultCurrency,
        security_deposit: lease.security_deposit ?? "",
        pet_deposit: lease.pet_deposit ?? "0",
        pets_allowed: !!lease.pets_allowed,
        late_fee_amount: lease.late_fee_amount ?? "0",
        late_fee_grace_days: lease.late_fee_grace_days ?? 5,
        utilities_included: lease.utilities_included || [],
        notes: lease.notes || "",
      });
    } else if (open && !lease) {
      setForm((p) => ({ ...p, currency: defaultCurrency, tenant_id: tenants?.[0]?.id || "" }));
    }
  }, [open, lease, tenants, defaultCurrency]);

  const handle = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleUtility = (u) =>
    setForm((p) => ({
      ...p,
      utilities_included: p.utilities_included.includes(u)
        ? p.utilities_included.filter((x) => x !== u)
        : [...p.utilities_included, u],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tenant_id) {
      toast.error("Pick a tenant first (add one in the Tenant tab)");
      return;
    }
    if (!form.start_date || !form.end_date || !form.monthly_rent) {
      toast.error("Start date, end date, and monthly rent are required");
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      toast.error("End date must be after start date");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        property_id: propertyId,
        monthly_rent: Number(form.monthly_rent),
        security_deposit: Number(form.security_deposit || 0),
        pet_deposit: Number(form.pet_deposit || 0),
        late_fee_amount: Number(form.late_fee_amount || 0),
        late_fee_grace_days: Number(form.late_fee_grace_days || 0),
      };
      if (editing) {
        await api.patch(`/manage/leases/${lease.id}`, payload);
        toast.success("Lease updated");
      } else {
        await api.post("/manage/leases", payload);
        toast.success("Lease created");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save lease");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-slate-200 bg-white" data-testid="lease-form-modal">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit lease" : "Start a new lease"}</DialogTitle>
          <DialogDescription className="text-slate-500">
            All amounts in {form.currency}. Status auto-derives from dates after save.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Tenant *</Label>
            <Select value={form.tenant_id} onValueChange={(v) => handle("tenant_id", v)} disabled={editing}>
              <SelectTrigger data-testid="lease-form-tenant"><SelectValue placeholder="Select tenant..." /></SelectTrigger>
              <SelectContent>
                {(tenants || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} · {t.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(tenants || []).length === 0 && (
              <p className="mt-1 text-xs text-red-600">No tenants on this property yet. Add one in the Tenant tab first.</p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="l-start">Start date *</Label>
              <Input id="l-start" type="date" data-testid="lease-form-start" value={form.start_date} onChange={(e) => handle("start_date", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="l-end">End date *</Label>
              <Input id="l-end" type="date" data-testid="lease-form-end" value={form.end_date} onChange={(e) => handle("end_date", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="l-rent">Monthly rent *</Label>
              <Input id="l-rent" type="number" step="0.01" data-testid="lease-form-rent" value={form.monthly_rent} onChange={(e) => handle("monthly_rent", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="l-ccy">Currency</Label>
              <Input id="l-ccy" maxLength={3} value={form.currency} onChange={(e) => handle("currency", e.target.value.toUpperCase())} />
            </div>
            <div>
              <Label htmlFor="l-dep">Security deposit</Label>
              <Input id="l-dep" type="number" step="0.01" value={form.security_deposit} onChange={(e) => handle("security_deposit", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="l-pdep">Pet deposit</Label>
              <Input id="l-pdep" type="number" step="0.01" value={form.pet_deposit} onChange={(e) => handle("pet_deposit", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="l-fee">Late fee amount</Label>
              <Input id="l-fee" type="number" step="0.01" value={form.late_fee_amount} onChange={(e) => handle("late_fee_amount", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="l-grace">Grace days</Label>
              <Input id="l-grace" type="number" value={form.late_fee_grace_days} onChange={(e) => handle("late_fee_grace_days", e.target.value)} />
            </div>
          </div>

          <div>
            <Label>Utilities included</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {UTIL_OPTIONS.map((u) => (
                <button
                  type="button"
                  key={u}
                  data-testid={`lease-util-${u}`}
                  onClick={() => toggleUtility(u)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                    form.utilities_included.includes(u)
                      ? "border border-indigo-200 bg-indigo-50 text-indigo-700"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="l-pets" checked={form.pets_allowed} onCheckedChange={(v) => handle("pets_allowed", !!v)} />
            <Label htmlFor="l-pets" className="cursor-pointer">Pets allowed</Label>
          </div>

          <div>
            <Label htmlFor="l-notes">Notes</Label>
            <Textarea id="l-notes" rows={3} value={form.notes} onChange={(e) => handle("notes", e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" className="bg-indigo-600 text-white hover:bg-indigo-700" disabled={saving} data-testid="lease-form-submit">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editing ? "Save changes" : "Create lease"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
